/**
 * A TELA QUE SE ATUALIZA SOZINHA: a hora da última busca, o botão Atualizar, e a busca nova a
 * cada minuto enquanto a aba está à vista.
 *
 * Achado da auditora, de 17/10/2026: o Início e a prévia ficaram 113 e 161 segundos com os totais
 * de antes, até alguém recarregar a página. Decisão do Cristiano: o Início e as prévias mostram
 * "atualizado às HH:MM", têm o botão Atualizar, e se atualizam sozinhos a cada 60 segundos
 * enquanto a aba está visível.
 *
 * **Aba escondida não busca nada**: o celular no bolso não gasta bateria nem dados. Quando a aba
 * volta à vista e a última busca tem mais de um minuto, busca na hora.
 *
 * O que chega atrasado do lado do notebook não muda com isto: o notebook manda a fila 3 segundos
 * depois de lançar, a cada 15 minutos, ao abrir e ao fechar. Esta tela só deixa de esperar alguém
 * recarregar.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'

export const SEGUNDOS_ENTRE_ATUALIZACOES = 60

export interface Atualizacao {
  /** Muda a cada busca pedida; entra nas dependências do efeito que busca. */
  rodada: number
  atualizadoEm: Date | null
  /** Quem buscou chama quando a resposta chegou. */
  marcarAtualizado: () => void
  /** O botão. */
  atualizar: () => void
}

export function useAtualizarSozinho(ligado = true): Atualizacao {
  const [rodada, setRodada] = useState(0)
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null)
  const ultima = useRef<number>(0)

  const atualizar = useCallback(() => setRodada((r) => r + 1), [])
  const marcarAtualizado = useCallback(() => {
    const agora = new Date()
    ultima.current = agora.getTime()
    setAtualizadoEm(agora)
  }, [])

  useEffect(() => {
    if (!ligado) return
    const aVista = (): boolean => document.visibilityState === 'visible'
    const passouOMinuto = (): boolean => Date.now() - ultima.current >= SEGUNDOS_ENTRE_ATUALIZACOES * 1000

    const relogio = window.setInterval(() => {
      if (aVista() && passouOMinuto()) atualizar()
    }, 5_000)
    const voltou = (): void => {
      if (aVista() && passouOMinuto()) atualizar()
    }
    document.addEventListener('visibilitychange', voltou)
    return () => {
      window.clearInterval(relogio)
      document.removeEventListener('visibilitychange', voltou)
    }
  }, [ligado, atualizar])

  return { rodada, atualizadoEm, marcarAtualizado, atualizar }
}

const comoHora = (d: Date): string =>
  d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

export function AtualizadoAs({ atualizacao }: { atualizacao: Atualizacao }): JSX.Element | null {
  if (atualizacao.atualizadoEm === null) return null
  return (
    <p className="atualizado-as so-tela" aria-live="polite">
      <span>
        Atualizado às <strong>{comoHora(atualizacao.atualizadoEm)}</strong>. A tela se atualiza
        sozinha a cada minuto.
      </span>{' '}
      <button className="mini" onClick={atualizacao.atualizar}>Atualizar</button>
    </p>
  )
}
